import { useState } from "react";
import {
    NumberInput,
    ReferenceInput,
    TextInput,
    required,
    Form,
    Toolbar,
    useUpdate,
    useTranslate,
    useRecordContext,
    useRefresh,
    useDataProvider,
    useNotify,
} from "react-admin";
import { Box, Button, Typography } from "@mui/material";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { RaAutocompleteInput } from "../../../../components/inputs/Ra/RaAutocompleteInput";
import { SerialNumberField } from "../../../../arrivals/components/SerialNumberField";

const getRelationId = (value) => (value && typeof value === "object" ? value.id : value);

export const CreateSkuForm = (props) => {
    const [update] = useUpdate();
    const translate = useTranslate();
    const refresh = useRefresh();
    const notify = useNotify();
    const productRecord = useRecordContext();
    const dataProvider = useDataProvider();
    const [serialNumbers, setSerialNumbers] = useState([]);

    const submit = (e) => {
        const supplierId = getRelationId(props.arrivalData?.supplier);

        if (props.skuRecord) {
            update(
                "skus",
                {
                    id: props.skuRecord.id,
                    data: {
                        ...props.skuRecord,
                        product: productRecord,
                        manufacturer: e.manufacturer,
                        ...(supplierId && {
                            supplier: { id: supplierId },
                        }),
                        storageLocation: e.storageLocation,
                        weight: e.weight,
                        serialNumber: e.serialNumber,
                    },
                    previousData: props.skuRecord,
                },
                {
                    onSuccess: () => {
                        props.setDeliveryWindow("show");
                        props.setSkuRecord();
                    },
                    onError: (e) => {
                        handleHttpError(e, notify);
                    },
                }
            );
        } else {
            const requestData = [];

            for (let i = 0; i < serialNumbers.length; i++) {
                requestData.push({
                    arrival: props.arrivalData,
                    product: productRecord,
                    manufacturer: e.manufacturer,
                    ...(supplierId && { supplier: { id: supplierId } }),
                    storageLocation: e.storageLocation,
                    weight: e.weight,
                    serialNumber: serialNumbers[i],
                });
            }

            dataProvider
                .createMany("skus", {
                    data: requestData,
                })
                .then(() => {
                    refresh();
                    props.setDeliveryWindow("show");
                })
                .catch((e) => {
                    handleHttpError(e, notify);
                });
        }
    };

    return (
        <Form
            onSubmit={submit}
            record={{
                ...props.skuRecord,
            }}
        >
            <Box
                sx={{
                    px: 3,
                    pt: 2
                }}>
                <Box sx={{
                    mb: 2
                }}>
                    <Typography variant="body1">
                        {translate(props.skuRecord ? "equip.editSku" : "equip.createSku")}
                    </Typography>
                </Box>

                <ReferenceInput
                    source="manufacturer.id"
                    reference="manufacturers"
                    sort={{ field: "name", order: "ASC" }}
                >
                    <RaAutocompleteInput
                        label="equip.manufacturer"
                        validate={[required()]}
                        field="name"
                    />
                </ReferenceInput>
                <ReferenceInput
                    source="storageLocation.id"
                    reference="storageLocations"
                    sort={{ field: "name", order: "ASC" }}
                >
                    <RaAutocompleteInput label="equip.storageLocation" field="name" />
                </ReferenceInput>
                <NumberInput
                    title={translate("equip.weightPerItem")}
                    label="equip.weightPerItem"
                    source="weight"
                />
                {props.skuRecord ? (
                    <TextInput
                        title={translate("equip.serialNumber")}
                        label="equip.serialNumber"
                        source="serialNumber"
                    />
                ) : (
                    <Box
                        sx={{
                            minHeight: "33vh",
                            maxHeight: "50vh",
                            display: "flex",
                            flexDirection: "column",
                            height: "calc(100vh - 450px)",
                        }}
                    >
                        <SerialNumberField
                            serialNumbers={serialNumbers}
                            setSerialNumbers={setSerialNumbers}
                        />
                    </Box>
                )}
            </Box>
            <Toolbar
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    background: "inherit",
                }}
            >
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                        size="small"
                        variant="contained"
                        type="submit"
                        title={translate("equip.save")}
                        aria-label={translate("equip.save")}
                    >
                        {translate("ra.action.save")}
                    </Button>
                    <Button
                        size="small"
                        type="button"
                        title={translate("equip.back")}
                        aria-label={translate("equip.back")}
                        onClick={() => props.setDeliveryWindow("show")}
                    >
                        {translate("ra.action.back")}
                    </Button>
                </Box>
                <Button
                    onClick={() => {
                        props.setDeliveryWindow("");
                    }}
                    title={translate("equip.close")}
                    aria-label={translate("equip.close")}
                >
                    {translate("equip.close")}
                </Button>
            </Toolbar>
        </Form>
    );
};
